import { useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronDown, HelpCircle, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type {
  MetadataSchema,
  MetadataSchemaField,
  MetadataValue,
  MetadataCategoryDropdown,
} from '@/types/metadata.types';
import { parseVisibility, evaluateVisibility } from './visibilityPredicate';
import TextInputField from './fields/TextInputField';
import TextAreaField from './fields/TextAreaField';
import NumberField from './fields/NumberField';
import DateField from './fields/DateField';
import SelectField from './fields/SelectField';
import MultiSelectField from './fields/MultiSelectField';
import SwitchField from './fields/SwitchField';

export interface DynamicMetadataFormProps {
  schema: MetadataSchema;
  values?: MetadataValue[];
  categories?: MetadataCategoryDropdown[];
  onChange?: (values: MetadataValue[]) => void;
  onValidationChange?: (isValid: boolean, errors: Record<string, string>) => void;
  readOnly?: boolean;
  showCategories?: boolean;
  className?: string;
}

interface FieldComponentProps {
  field: MetadataSchemaField;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
  disabled?: boolean;
}

// Field component resolver
function FieldComponent({ field, value, onChange, error, disabled }: FieldComponentProps) {
  const commonProps = {
    field,
    value,
    onChange,
    error,
    disabled: disabled || field.isReadOnly,
  };

  switch (field.fieldTypeCode) {
    case 'TEXT':
      return <TextInputField {...commonProps} />;
    case 'TEXTAREA':
      return <TextAreaField {...commonProps} />;
    case 'NUMBER':
    case 'DECIMAL':
      return <NumberField {...commonProps} isDecimal={field.fieldTypeCode === 'DECIMAL'} />;
    case 'DATE':
      return <DateField {...commonProps} />;
    case 'DATETIME':
      return <DateField {...commonProps} includeTime />;
    case 'DROPDOWN':
      return <SelectField {...commonProps} />;
    case 'MULTISELECT':
      return <MultiSelectField {...commonProps} />;
    case 'BOOLEAN':
      return <SwitchField {...commonProps} />;
    default:
      return <div className="text-sm text-muted-foreground">Unsupported field type: {field.fieldTypeCode}</div>;
  }
}

// Build validation rules for react-hook-form
function buildValidationRules(field: MetadataSchemaField) {
  const rules: Record<string, unknown> = {};

  if (field.isRequired) {
    rules.required = `${field.label} is required`;
  }

  const validationRules = field.validationRules;
  if (validationRules) {
    if (validationRules.minLength !== undefined) {
      rules.minLength = {
        value: validationRules.minLength,
        message: `${field.label} must be at least ${validationRules.minLength} characters`,
      };
    }
    if (validationRules.maxLength !== undefined) {
      rules.maxLength = {
        value: validationRules.maxLength,
        message: `${field.label} must be no more than ${validationRules.maxLength} characters`,
      };
    }
    if (validationRules.min !== undefined) {
      rules.min = {
        value: validationRules.min,
        message: `${field.label} must be at least ${validationRules.min}`,
      };
    }
    if (validationRules.max !== undefined) {
      rules.max = {
        value: validationRules.max,
        message: `${field.label} must be no more than ${validationRules.max}`,
      };
    }
    if (validationRules.regex) {
      rules.pattern = {
        value: new RegExp(validationRules.regex),
        message: validationRules.regexMessage || `${field.label} has an invalid format`,
      };
    }
  }

  return rules;
}

// Group fields by category
function groupFieldsByCategory(
  fields: MetadataSchemaField[],
  categories?: MetadataCategoryDropdown[]
): { category: MetadataCategoryDropdown | null; fields: MetadataSchemaField[] }[] {
  const groups: Map<number | null, MetadataSchemaField[]> = new Map();

  // Sort fields by sortOrder and filter active/visible
  const sortedFields = [...fields]
    .filter((f) => f.isActive && f.isVisible)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  sortedFields.forEach((field) => {
    const categoryId = field.metadataCategoryId || null;
    if (!groups.has(categoryId)) {
      groups.set(categoryId, []);
    }
    groups.get(categoryId)!.push(field);
  });

  const result: { category: MetadataCategoryDropdown | null; fields: MetadataSchemaField[] }[] = [];

  // Add uncategorized fields first
  if (groups.has(null)) {
    result.push({ category: null, fields: groups.get(null)! });
    groups.delete(null);
  }

  // Add categorized fields
  groups.forEach((fields, categoryId) => {
    const category = categories?.find((c) => c.metadataCategoryId === categoryId) || null;
    result.push({ category, fields });
  });

  return result;
}

// Convert MetadataValue[] to form values object
function valuesToFormData(values?: MetadataValue[]): Record<string, unknown> {
  if (!values) return {};
  const formData: Record<string, unknown> = {};
  values.forEach((v) => {
    formData[v.fieldKey] = v.value;
  });
  return formData;
}

// Convert form values object to MetadataValue[]
function formDataToValues(
  formData: Record<string, unknown>,
  fields: MetadataSchemaField[]
): MetadataValue[] {
  return fields
    .filter((f) => f.isActive && f.isVisible)
    .map((field) => ({
      fieldKey: field.fieldKey,
      metadataSchemaFieldId: field.metadataSchemaFieldId,
      fieldLabel: field.label,
      fieldTypeCode: field.fieldTypeCode,
      value: formData[field.fieldKey],
    }));
}

export default function DynamicMetadataForm({
  schema,
  values,
  categories,
  onChange,
  onValidationChange,
  readOnly = false,
  showCategories = true,
  className,
}: DynamicMetadataFormProps) {
  const defaultValues = useMemo(() => valuesToFormData(values), [values]);

  const {
    control,
    watch,
    formState: { errors, isValid },
  } = useForm({
    defaultValues,
    mode: 'onChange',
  });

  const formValues = watch();

  // Notify parent of value changes
  useEffect(() => {
    if (onChange) {
      const metadataValues = formDataToValues(formValues, schema.fields);
      onChange(metadataValues);
    }
  }, [formValues, onChange, schema.fields]);

  // Notify parent of validation state
  useEffect(() => {
    if (onValidationChange) {
      const errorMessages: Record<string, string> = {};
      Object.entries(errors).forEach(([key, error]) => {
        if (error?.message) {
          errorMessages[key] = error.message as string;
        }
      });
      onValidationChange(isValid, errorMessages);
    }
  }, [isValid, errors, onValidationChange]);

  // Group fields
  const groupedFields = useMemo(
    () => groupFieldsByCategory(schema.fields, categories),
    [schema.fields, categories]
  );

  // Check conditional visibility. Phase 8 #7 — when visibleWhenJson is
  // set, it supersedes the legacy single-field equality pair. Both
  // forms coexist so existing schemas keep working unchanged.
  const isFieldVisible = (field: MetadataSchemaField): boolean => {
    const predicate = parseVisibility(
      (field as MetadataSchemaField & { visibleWhenJson?: string | null }).visibleWhenJson
    );
    if (predicate) return evaluateVisibility(predicate, formValues);

    if (!field.dependsOnFieldKey) return true;
    const dependsOnValue = formValues[field.dependsOnFieldKey];
    if (field.dependsOnValue === undefined || field.dependsOnValue === null) {
      // Just check if the field has any value
      return !!dependsOnValue;
    }
    // Check if value matches
    if (typeof dependsOnValue === 'boolean') {
      return dependsOnValue.toString() === field.dependsOnValue;
    }
    return dependsOnValue === field.dependsOnValue;
  };

  // Render a single field
  const renderField = (field: MetadataSchemaField) => {
    if (!isFieldVisible(field)) return null;

    const colSpanClass =
      field.colSpan === 3
        ? 'md:col-span-3'
        : field.colSpan === 2
          ? 'md:col-span-2'
          : 'md:col-span-1';

    const error = errors[field.fieldKey]?.message as string | undefined;

    return (
      <div key={field.fieldKey} className={cn('space-y-2', colSpanClass)}>
        <div className="flex items-center gap-2">
          <Label htmlFor={field.fieldKey} className="flex items-center gap-1">
            {field.label}
            {field.isRequired && <span className="text-destructive">*</span>}
          </Label>
          {field.helpText && (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="h-4 w-4 p-0">
                  <HelpCircle className="h-3 w-3 text-muted-foreground" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 text-sm" side="top">
                {field.helpText}
              </PopoverContent>
            </Popover>
          )}
        </div>

        <Controller
          name={field.fieldKey}
          control={control}
          rules={buildValidationRules(field)}
          render={({ field: controllerField }) => (
            <FieldComponent
              field={field}
              value={controllerField.value}
              onChange={controllerField.onChange}
              error={error}
              disabled={readOnly}
            />
          )}
        />

        {error && (
          <p className="text-xs text-destructive flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            {error}
          </p>
        )}
      </div>
    );
  };

  if (schema.fields.length === 0) {
    return (
      <div className={cn('text-center py-8 text-muted-foreground', className)}>
        <p>No metadata fields defined for this schema.</p>
      </div>
    );
  }

  // Render without category grouping
  if (!showCategories) {
    return (
      <div className={cn('grid grid-cols-1 md:grid-cols-3 gap-4', className)}>
        {schema.fields
          .filter((f) => f.isActive && f.isVisible)
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map(renderField)}
      </div>
    );
  }

  // Render with category grouping
  return (
    <div className={cn('space-y-6', className)}>
      {groupedFields.map(({ category, fields }) => {
        const categoryKey = category?.code || 'uncategorized';
        const visibleFields = fields.filter(isFieldVisible);

        if (visibleFields.length === 0) return null;

        if (category) {
          return (
            <Collapsible key={categoryKey} defaultOpen>
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full justify-between p-2 h-auto"
                  style={{ borderLeft: `3px solid ${category.colorCode || '#6B7280'}` }}
                >
                  <span className="font-medium">{category.name}</span>
                  <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 pl-4 border-l-2 border-muted ml-1.5">
                  {visibleFields.map(renderField)}
                </div>
              </CollapsibleContent>
            </Collapsible>
          );
        }

        // Uncategorized fields
        return (
          <div key={categoryKey} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {visibleFields.map(renderField)}
          </div>
        );
      })}
    </div>
  );
}

// Loading skeleton
export function DynamicMetadataFormSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-10 w-full" />
          </div>
        ))}
      </div>
    </div>
  );
}
